import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class JwtInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Get token directly from localStorage/sessionStorage to avoid circular dependency
    const token = this.getStoredToken();
    // CRAT-1302 — background polls (e.g. NotificationsService) mark themselves silent so an
    // expired token doesn't reload the page mid-workflow; the marker never reaches the API.
    const silent401 = req.headers.has('X-Cratebase-Silent-401');

    return of(token).pipe(
      mergeMap((token) => {
        let headers = req.headers;
        if (token) {
          headers = req.headers.append('Authorization', `Bearer ${token}`);
        }
        if (silent401) {
          headers = headers.delete('X-Cratebase-Silent-401');
        }
        if (
          !req.url.startsWith('/assets') &&
          !req.url.startsWith('assets') &&
          !req.url.startsWith('https://') &&
          !req.url.startsWith('http://')
        ) {
          const url = req.url.startsWith('admin')
            ? `${environment.apiAdminBaseUrl}/${req.url}`
            : `${environment.apiBaseUrl}/${req.url}`;

          const apiReq = req.clone({
            url,
            headers,
          });
          return next.handle(apiReq);
        }
        return next.handle(req);
      }),
      catchError((err) => {
        const { status } = err;
        console.log('JWT Interceptor caught error:', {
          status,
          url: req.url,
          isExternal: req.url.startsWith('https://') || req.url.startsWith('http://'),
          error: err,
        });
        if (status === 401) {
          if (silent401) {
            return throwError(() => err);
          }
          // Only clear storage for internal API calls, not external ones like Discogs
          if (!req.url.startsWith('https://') && !req.url.startsWith('http://')) {
            console.log('Clearing localStorage for internal 401 error');
            this.clearStoredToken();
          } else {
            console.log('Ignoring 401 from external API:', req.url);
          }
        }
        throw err;
      })
    );
  }

  private getStoredToken(): string | null {
    // Check both localStorage and sessionStorage to match AuthService behavior
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  }

  private clearStoredToken(): void {
    // Clear from both storages and reload page to match AuthService behavior
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    window.location.reload();
  }
}
